import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useEffect, useState } from "react"
import TextTransition, { presets } from "react-text-transition"
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion"

const ROTATION_MS = 3_000

/**
 * Extraction steps rotated while the AI analysis runs. These describe real
 * pipeline work (structure analysis, column detection, entity matching) —
 * never invent a step the API already reports as finished.
 */
const EXTRACTION_STEP_KEYS = [
  "MC_MD_PROC_STEP_STRUCTURE",
  "MC_MD_PROC_STEP_COLUMNS",
  "MC_MD_PROC_STEP_SUPPLIERS",
  "MC_MD_PROC_STEP_ARTICLES",
  "MC_MD_PROC_STEP_SUBSTANCES",
  "MC_MD_PROC_STEP_DUPLICATES",
] as const

interface ProcessingTickerProps {
  /** True while any file is still converting (pending/parsing). */
  converting: boolean
}

/**
 * Rotating status line under the processing spinner. While files convert it
 * shows a fixed "preparing" line; afterwards it cycles through the extraction
 * steps with a crossfade. Purely visual (the static section label above
 * carries the state for assistive tech), hence aria-hidden.
 */
export function ProcessingTicker({ converting }: ProcessingTickerProps) {
  const { translate } = useTranslation()
  const [stepIndex, setStepIndex] = useState(0)

  useEffect(() => {
    if (converting) return undefined
    const id = setInterval(
      () => setStepIndex((i) => (i + 1) % EXTRACTION_STEP_KEYS.length),
      ROTATION_MS,
    )
    return () => clearInterval(id)
  }, [converting])

  const text = translate(
    converting ? "MC_MD_PROC_STEP_CONVERTING" : EXTRACTION_STEP_KEYS[stepIndex],
  )

  const prefersReducedMotion = usePrefersReducedMotion()

  return (
    <div aria-hidden="true" className="w-full text-center text-sm text-grey5">
      {prefersReducedMotion ? (
        <span>{text}</span>
      ) : (
        <TextTransition
          springConfig={presets.gentle}
          className="justify-center"
        >
          {text}
        </TextTransition>
      )}
    </div>
  )
}
